import React, { useState, useEffect, useCallback } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import styles from './Navigation.module.css';
import useMediaQuery from '../../hooks/useMediaQuery';
import type { IconType } from 'react-icons';
import {
  FaAppleAlt,
  FaHome,
  FaUtensils,
  FaListUl,
  FaAddressBook,
  FaBars,
  FaTimes,
  FaWallet,
  FaTools,
} from 'react-icons/fa';
import { IconButton } from '../ui/Button';
import Login from '../Login';
import { ThemeToggle } from '../ui/ThemeToggle';
import { useAdmin } from '../../context/AdminContext';

interface NavItem {
  to: string;
  label: string;
  icon: IconType;
  end?: boolean;
}

const primaryNavItems: NavItem[] = [
  { to: '/', label: 'Home', icon: FaHome, end: true },
  { to: '/todos', label: 'Tasks', icon: FaListUl },
  { to: '/nutrition', label: 'Nutrition', icon: FaAppleAlt },
  { to: '/recipes', label: 'Recipes', icon: FaUtensils },
  { to: '/contacts', label: 'CRM', icon: FaAddressBook },
  { to: '/budget', label: 'Budget', icon: FaWallet },
];

const adminNavItems: NavItem[] = [
  { to: '/admin/agent-tasks', label: 'Agent Tasks', icon: FaTools },
];

const Navigation: React.FC = () => {
  const location = useLocation();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const { isAdmin } = useAdmin();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  
  // Close menu when route changes
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  // Close menu when switching from mobile to desktop
  useEffect(() => {
    if (!isMobile) {
      setIsMenuOpen(false);
    }
  }, [isMobile]);

  // Handle escape key to close menu
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (event.key === 'Escape' && isMenuOpen) {
      setIsMenuOpen(false);
    }
  }, [isMenuOpen]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const renderDesktopNavItems = (items: NavItem[]) => (
    <ul className={styles.navList}>
      {items.map((item) => {
        const Icon = item.icon;
        return (
          <li className={styles.navItem} key={item.to}>
            <NavLink
              to={item.to}
              className={({ isActive }) =>
                isActive ? `${styles.navLink} ${styles.activeLink}` : styles.navLink
              }
              end={item.end}
            >
              <span className={styles.navIcon}>
                {Icon({ 'aria-hidden': 'true' })}
              </span>
              {item.label}
            </NavLink>
          </li>
        );
      })}
    </ul>
  );

  const renderMobileNavItems = (items: NavItem[], listClassName?: string) => (
    <ul className={`${styles.sideMenuList} ${listClassName ?? ''}`.trim()}>
      {items.map((item) => {
        const Icon = item.icon;
        return (
          <li className={styles.sideMenuItem} key={item.to}>
            <NavLink
              to={item.to}
              className={({ isActive }) =>
                isActive ? `${styles.sideMenuLink} ${styles.sideMenuActiveLink}` : styles.sideMenuLink
              }
              onClick={closeMenu}
              end={item.end}
            >
              <span className={styles.sideMenuIcon}>
                {Icon({ 'aria-hidden': 'true' })}
              </span>
              <span className={styles.sideMenuText}>{item.label}</span>
            </NavLink>
          </li>
        );
      })}
    </ul>
  );

  // Desktop sidebar navigation
  const desktopNav = (
    <nav className={styles.navigation} aria-label="Main Navigation">
      {renderDesktopNavItems(primaryNavItems)}

      {isAdmin && (
        <section className={styles.menuSection} aria-label="Admin Tools">
          <p className={styles.menuSectionLabel}>Admin Tools</p>
          {renderDesktopNavItems(adminNavItems)}
        </section>
      )}

      <section className={`${styles.menuSection} ${styles.menuSectionEnd}`} aria-label="Account">
        <p className={styles.menuSectionLabel}>Account</p>
        <div className={styles.menuThemeRow}>
          <span className={styles.menuThemeLabel}>Theme</span>
          <ThemeToggle />
        </div>
        <Login variant="sidebar" />
      </section>
    </nav>
  );

  // Mobile navigation (side menu)
  const mobileNav = (
    <>
      {!isMenuOpen && (
        <IconButton
          icon={FaBars({ 'aria-hidden': 'true' })}
          variant="secondary"
          size="md"
          onClick={toggleMenu}
          aria-label="Open menu"
          aria-expanded="false"
          aria-controls="mobile-side-menu"
          className={styles.floatingMenuButton}
        />
      )}

      {/* Overlay */}
      <div
        className={`${styles.overlay} ${isMenuOpen ? styles.overlayVisible : ''}`}
        onClick={closeMenu}
        aria-hidden="true"
        data-testid="side-menu-overlay"
      />

      {/* Side menu */}
      <nav
        id="mobile-side-menu"
        className={`${styles.sideMenu} ${isMenuOpen ? styles.sideMenuOpen : ''}`}
        aria-label="Mobile Navigation"
        aria-hidden={!isMenuOpen}
        data-testid="mobile-side-menu"
      >
        <div className={styles.sideMenuHeader}>
          <span className={styles.sideMenuTitle}>Organizer</span>
          <IconButton
            icon={FaTimes({ 'aria-hidden': 'true' })}
            variant="ghost"
            size="sm"
            onClick={closeMenu}
            aria-label="Close menu"
          />
        </div>
        {renderMobileNavItems(primaryNavItems, styles.sideMenuPrimaryList)}

        {isAdmin && (
          <section className={styles.sideMenuSection} aria-label="Admin Tools">
            <p className={styles.sideMenuSectionLabel}>Admin Tools</p>
            {renderMobileNavItems(adminNavItems)}
          </section>
        )}

        <section className={styles.sideMenuSection} aria-label="Account">
          <p className={styles.sideMenuSectionLabel}>Account</p>
          <div className={styles.sideMenuThemeRow}>
            <span className={styles.sideMenuThemeLabel}>Theme</span>
            <ThemeToggle />
          </div>
          <Login variant="sidebar" />
        </section>
      </nav>
    </>
  );

  // Return the appropriate navigation based on screen size
  return isMobile ? mobileNav : desktopNav;
};

export default Navigation;
